import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, ProgressBar, Row, Spinner, Alert, Badge, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ReactApexChart from "react-apexcharts";
import { formatNumber, formatCurrency } from "../utils/formatters";
import { exportCAtoExcel } from "../utils/excelExport1";
import { generateCAPDF } from "../utils/pdfExport1";
import './Dashboard.css';
import { API_BASE_URL } from "../constants/api";

export default function FinanceMonitoring() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/stats_dashboard.php`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const data = await response.json();
      console.log("📊 Données reçues:", data);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalVentes = () => {
    if (!stats) return 0;
    if (stats.ca_annuel?.[0]?.nombre_factures) return stats.ca_annuel[0].nombre_factures;
    if (stats.top_clients) return stats.top_clients.reduce((acc, client) => acc + (client.nb_factures || 0), 0);
    return 0;
  };

  const totalVentes = getTotalVentes();

  const seriesCA = [{
    name: 'Chiffre d\'affaires',
    data: stats?.ca_mensuel || Array(12).fill(0)
  }];

  const optionsCA = {
    chart: { type: 'area', height: 350, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    colors: ['#4361ee'],
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.2 } },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
    xaxis: { categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'], labels: { style: { fontSize: '11px', fontWeight: 500 } } },
    yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } }
  };

  const seriesIncomeExpenses = [
    { name: 'Revenus', type: 'column', data: stats?.income_expenses?.map(item => parseFloat(item.revenus)) || [] },
    { name: 'Dépenses', type: 'column', data: stats?.income_expenses?.map(item => parseFloat(item.depenses)) || [] }
  ];

  const optionsIncomeExpenses = {
    chart: { stacked: true, toolbar: { show: false }, height: 350, fontFamily: 'Inter, system-ui, sans-serif' },
    colors: ['#4361ee', '#f59e0b'],
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 8 } },
    grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
    xaxis: {
      categories: stats?.income_expenses?.map(item => {
        const [year, month] = item.mois.split('-');
        const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
        return `${months[parseInt(month)-1]} ${year}`;
      }) || [],
      labels: { style: { fontSize: '10px', fontWeight: 500 } }
    },
    yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } }
  };

  const seriesCategories = stats?.ventes_categories?.map(item => parseFloat(item.montant_total)) || [];
  const optionsDonut = {
    chart: { type: 'donut', height: 280, fontFamily: 'Inter, system-ui, sans-serif' },
    labels: stats?.ventes_categories?.map(item => item.nom_categorie || 'Sans catégorie') || [],
    colors: ['#4361ee', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    legend: { position: 'bottom', fontSize: '11px', offsetY: 5 },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '14px',
              fontWeight: 600,
              formatter: () => formatCurrency(seriesCategories.reduce((a, b) => a + b, 0))
            }
          }
        }
      }
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const exportData = {
        periode: { annee: selectedYear, mois: new Date().toLocaleString('fr-FR', { month: 'long' }) },
        totaux: {
          ca_annuel: stats.totaux?.ca_annuel || 0,
          ca_mensuel: stats.totaux?.ca_mensuel || 0,
          nb_clients: stats.totaux?.nb_clients || 0,
          nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
          nb_produits: stats.totaux?.nb_produits || 0,
          creances_total: stats.totaux?.creances_total || 0
        },
        ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
        mois: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        top_produits: stats.top_produits || [],
        top_clients: stats.top_clients || [],
        ventes_categories: stats.ventes_categories || [],
        income_expenses: stats.income_expenses || [],
        stock_alertes: stats.stock_alertes || [],
        profit_margins: stats.profit_margins || {},
        ratios: stats.ratios || {}
      };
      const success = await exportCAtoExcel(exportData, selectedYear);
      if (!success) alert("Erreur lors de l'export Excel");
    } catch (error) {
      console.error("Erreur export Excel:", error);
      alert("Erreur lors de l'export Excel: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const exportData = {
        titre: "Rapport Financier",
        periode: { annee: selectedYear, mois: new Date().toLocaleString('fr-FR', { month: 'long' }) },
        totaux: {
          ca_annuel: stats.totaux?.ca_annuel || 0,
          ca_mensuel: stats.totaux?.ca_mensuel || 0,
          nb_clients: stats.totaux?.nb_clients || 0,
          nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
          nb_produits: stats.totaux?.nb_produits || 0,
          creances_total: stats.totaux?.creances_total || 0
        },
        top_produits: stats.top_produits || [],
        top_clients: stats.top_clients || [],
        ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
        ventes_categories: stats.ventes_categories || [],
        income_expenses: stats.income_expenses || [],
        stock_alertes: stats.stock_alertes || []
      };
      await generateCAPDF(exportData, selectedYear);
    } catch (error) {
      console.error("Erreur export PDF:", error);
      alert("Erreur lors de l'export PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="loading-animation mb-4">
                <div className="loading-ring">
                  <div className="loading-ring-inner"></div>
                </div>
                <i className="ri-bar-chart-2-line loading-icon"></i>
              </div>
              <h5 className="fw-bold text-primary mb-2">Chargement des données financières</h5>
              <p className="text-muted small mb-0">Récupération des statistiques en cours...</p>
              <div className="mx-auto mt-4" style={{ width: '200px' }}>
                <div className="progress" style={{ height: '3px', borderRadius: '3px', background: '#e9ecef' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
          .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
          .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
          .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
          .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
          @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
          @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchDashboardStats}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* En-tête moderne */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                <i className="ri-bar-chart-2-line fs-20 text-primary"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">Tableau de bord financier</h4>
                <p className="text-muted small mb-0">Analyse des performances et indicateurs clés</p>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={true}
                onChange={() => {}}
                id="autoRefresh"
              />
              <label className="form-check-label small text-muted" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-primary" size="sm" className="rounded-pill px-3 d-flex align-items-center gap-1" disabled={exporting}>
                {exporting ? <Spinner size="sm" className="me-1" /> : <i className="ri-download-line"></i>}
                Exporter
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleExportExcel}>
                  <i className="ri-file-excel-line text-success me-2"></i> Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line text-danger me-2"></i> Export PDF
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* KPI Cards modernes - Style comme StockCritique */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon">
                <i className="ri-money-euro-circle-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">CA annuel</span>
                <h3 className="kpi-value">{formatCurrency(parseFloat(stats.totaux?.ca_annuel) || 0)}</h3>
                <span className="kpi-unit">{stats.ca_annuel?.[0]?.nombre_factures || totalVentes} factures</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon">
                <i className="ri-shopping-bag-3-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Ventes totales</span>
                <h3 className="kpi-value">{totalVentes}</h3>
                <span className="kpi-unit">CA mensuel: {formatCurrency(parseFloat(stats.totaux?.ca_mensuel) || 0)}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon">
                <i className="ri-group-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Clients actifs</span>
                <h3 className="kpi-value">{stats.totaux?.nb_clients || 0}</h3>
                <span className="kpi-unit">Fournisseurs: {stats.totaux?.nb_fournisseurs || 0}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-info">
              <div className="kpi-icon">
                <i className="ri-stack-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Produits en stock</span>
                <h3 className="kpi-value">{stats.totaux?.nb_produits || 0}</h3>
                <span className="kpi-unit">Top ventes: {stats.top_produits?.length || 0}</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Barre d'objectif mensuel */}
        <Card className="border-0 shadow-sm rounded-0 mb-4">
          <Card.Body className="py-3 px-4">
            <Row className="align-items-center">
              <Col md="8">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 bg-success bg-opacity-10 rounded-3">
                    <i className="ri-target-line fs-20 text-success"></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small fw-semibold">Objectif mensuel</span>
                      <span className="small fw-bold text-success">{Math.round((parseFloat(stats.totaux?.ca_mensuel) / 5000000) * 100)}%</span>
                    </div>
                    <ProgressBar now={Math.min((parseFloat(stats.totaux?.ca_mensuel) / 5000000) * 100, 100)} variant="success" style={{ height: '6px', borderRadius: '3px' }} />
                  </div>
                </div>
              </Col>
              <Col md="4">
                <div className="d-flex justify-content-md-end mt-3 mt-md-0">
                  <div className="text-center">
                    <span className="text-muted small d-block">Objectif 5 000 000 FCFA</span>
                    <span className="fw-bold text-primary">{formatCurrency(parseFloat(stats.totaux?.ca_mensuel) || 0)}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Graphiques principaux */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-bar-chart-line text-primary"></i>
                  <h6 className="mb-0 fw-semibold">Évolution du chiffre d'affaires</h6>
                </div>
                <Nav className="nav-icon">
                  <Nav.Link onClick={() => setSelectedYear(2026)} className={selectedYear === 2026 ? 'active' : ''} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>2026</Nav.Link>
                </Nav>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <ReactApexChart series={seriesCA} options={optionsCA} type="area" height={330} />
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-pie-chart-2-line text-success"></i>
                  <h6 className="mb-0 fw-semibold">Répartition des ventes</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <ReactApexChart series={seriesCategories} options={optionsDonut} type="donut" height={280} />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Revenus vs Dépenses + Marges */}
        {stats.income_expenses && stats.income_expenses.length > 0 && (
          <Row className="g-3 mb-4">
            <Col xl="7">
              <Card className="border-0 shadow-sm rounded-0">
                <Card.Header className="bg-transparent border-0 pt-3 px-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-bar-chart-grouped-line text-primary"></i>
                    <h6 className="mb-0 fw-semibold">Revenus vs Dépenses</h6>
                  </div>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                  <ReactApexChart series={seriesIncomeExpenses} options={optionsIncomeExpenses} type="bar" height={340} />
                </Card.Body>
              </Card>
            </Col>

            <Col xl="5">
              <Card className="border-0 shadow-sm rounded-0">
                <Card.Header className="bg-transparent border-0 pt-3 px-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-line-chart-line text-warning"></i>
                    <h6 className="mb-0 fw-semibold">Marges bénéficiaires</h6>
                  </div>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between small mb-2">
                      <span className="text-muted">Marge brute</span>
                      <span className="fw-bold">{stats.profit_margins?.gross || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.gross || 0} className="mb-3" style={{ height: '6px', borderRadius: '3px' }} />
                    
                    <div className="d-flex justify-content-between small mb-2">
                      <span className="text-muted">Marge opérationnelle</span>
                      <span className="fw-bold">{stats.profit_margins?.operating || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.operating || 0} variant="info" className="mb-3" style={{ height: '6px', borderRadius: '3px' }} />
                    
                    <div className="d-flex justify-content-between small mb-2">
                      <span className="text-muted">Marge nette</span>
                      <span className="fw-bold">{stats.profit_margins?.net || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.net || 0} variant="success" className="mb-3" style={{ height: '6px', borderRadius: '3px' }} />
                  </div>

                  <hr className="my-3" />

                  <Row className="g-3">
                    <Col xs="6">
                      <div className="text-center p-3 bg-light rounded-0">
                        <h6 className="mb-0 text-primary fw-bold" style={{ fontSize: '1.2rem' }}>{stats.ratios?.quick_ratio || 0}</h6>
                        <small className="text-muted">Quick Ratio</small>
                      </div>
                    </Col>
                    <Col xs="6">
                      <div className="text-center p-3 bg-light rounded-0">
                        <h6 className="mb-0 text-success fw-bold" style={{ fontSize: '1.2rem' }}>{stats.ratios?.current_ratio || 0}</h6>
                        <small className="text-muted">Current Ratio</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Top produits et top clients */}
        <Row className="g-3 mb-4">
          <Col xl="6">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-suitcase-2-line text-primary"></i>
                  <h6 className="mb-0 fw-semibold">Top {stats.top_produits?.length || 3} Produits</h6>
                </div>
                <Badge bg="primary" className="rounded-pill">Ventes</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Référence</th>
                        <th>Désignation</th>
                        <th className="text-end">Qté</th>
                        <th className="text-end pe-4">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_produits?.map((produit, index) => (
                        <tr key={index}>
                          <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                          <td className="fw-semibold">{produit.designation}</td>
                          <td className="text-end">{formatNumber(parseInt(produit.quantite_vendue))}</td>
                          <td className="text-end pe-4 text-primary fw-bold">{formatCurrency(parseFloat(produit.montant_total))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-group-line text-success"></i>
                  <h6 className="mb-0 fw-semibold">Top {stats.top_clients?.length || 3} Clients</h6>
                </div>
                <Badge bg="success" className="rounded-pill">CA</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Code</th>
                        <th>Client</th>
                        <th className="text-end">Factures</th>
                        <th className="text-end pe-4">Total achats</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_clients?.map((client, index) => (
                        <tr key={index}>
                          <td className="ps-4"><span className="reference-badge">{client.code_client}</span></td>
                          <td className="fw-semibold">{client.raison_sociale}</td>
                          <td className="text-end">{client.nb_factures}</td>
                          <td className="text-end pe-4 text-success fw-bold">{formatCurrency(parseFloat(client.total_achats))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alertes stock */}
        {stats.stock_alertes?.length > 0 && (
          <Card className="border-0 shadow-sm rounded-0 mb-4">
            <Card.Header className="bg-transparent border-0 pt-3 px-4">
              <div className="d-flex align-items-center gap-2">
                <i className="ri-alert-line text-danger"></i>
                <h6 className="mb-0 fw-semibold">Alertes stock critiques ({stats.stock_alertes.length})</h6>
              </div>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              <Row className="g-3">
                {stats.stock_alertes.slice(0, 3).map((alerte, idx) => (
                  <Col md="4" key={idx}>
                    <div className="alert-card p-3 rounded-0">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="reference-badge">{alerte.reference}</span>
                        <Badge bg="danger" className="rounded-pill">Stock bas</Badge>
                      </div>
                      <h6 className="fw-semibold mb-2">{alerte.designation}</h6>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted small">Stock actuel:</span>
                        <span className="fw-bold text-danger">{formatNumber(alerte.quantite_reelle)}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted small">Seuil critique:</span>
                        <span className="fw-bold">{formatNumber(alerte.seuil_alerte)}</span>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {stats.stock_alertes.length > 3 && (
                <div className="text-center mt-3">
                  <Link to="/stock/alertes" className="text-decoration-none">
                    Voir toutes les alertes ({stats.stock_alertes.length}) <i className="ri-arrow-right-line ms-1"></i>
                  </Link>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>

      <style>{`
        /* KPI Cards */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: #4361ee; }
        .kpi-success { border-left-color: #10b981; }
        .kpi-warning { border-left-color: #f59e0b; }
        .kpi-info { border-left-color: #0dcaf0; }
        
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
        .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
        .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
        .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
        
        .kpi-content {
          flex: 1;
        }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .kpi-unit {
          font-size: 0.7rem;
          color: #6c757d;
        }
        
        /* Reference Badge */
        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-family: monospace;
        }
        
        /* Alert Card */
        .alert-card {
          background: #fef2f2;
          border-left: 3px solid #ef4444;
          transition: all 0.2s;
        }
        .alert-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        
        .rounded-3 { border-radius: 0px !important; }
      `}</style>
    </React.Fragment>
  );
}